import React from 'react';
import {
  Container,
  Accordion,
  Card,
  Spinner,
  Alert,
  Form,
  Button,
  Row,
  Col,
} from 'react-bootstrap';
import { connect } from 'react-redux';
import { Link } from 'react-router-dom';
import PageHeading from '../PageHeading';
import PropTypes from 'prop-types';
import axios from 'axios';
import { logout } from '../../store';
import {
  formatAddressLines,
  readPendingAddress,
  clearPendingAddress,
  savePendingAddressIfAny,
} from '../../utils/pendingAddress';

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

const emptyAddressForm = {
  fullName: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  state: '',
  postalCode: '',
  country: 'US',
  label: '',
};

const formatCents = (cents) =>
  cents == null ? '—' : `$${(Number(cents) / 100).toFixed(2)}`;

const formatDate = (value) => {
  if (!value) return '';
  try {
    return new Date(value).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch (err) {
    return '';
  }
};

const statusLabel = (status) => {
  if (status === 'paid') return 'Paid';
  if (status === 'canceled') return 'Canceled';
  if (status === 'payment_failed') return 'Payment failed';
  return status;
};

class AccountInfo extends React.Component {
  constructor() {
    super();
    this.state = {
      orders: [],
      ordersLoading: true,
      ordersError: null,
      addresses: [],
      addressesLoading: true,
      addressesError: null,
      addressSuccess: null,
      showAddressForm: false,
      addressForm: { ...emptyAddressForm },
      savingAddress: false,
    };
  }

  componentDidMount() {
    this.loadOrders();
    this.loadAddresses();
    this.claimPendingAddress();
  }

  async claimPendingAddress() {
    if (!readPendingAddress()) return;
    await savePendingAddressIfAny(axios);
    if (!readPendingAddress()) {
      this.setState({
        addressSuccess: 'Your checkout address was saved on file.',
      });
      this.loadAddresses();
    }
  }

  async loadOrders() {
    this.setState({ ordersLoading: true, ordersError: null });
    try {
      const { data } = await axios.get('/api/orders');
      this.setState({ orders: data || [], ordersLoading: false });
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        err.message ||
        'Unable to load orders.';
      this.setState({ ordersLoading: false, ordersError: message });
    }
  }

  async loadAddresses() {
    this.setState({ addressesLoading: true, addressesError: null });
    try {
      const { data } = await axios.get('/api/addresses');
      this.setState({ addresses: data || [], addressesLoading: false });
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        err.message ||
        'Unable to load addresses.';
      this.setState({ addressesLoading: false, addressesError: message });
    }
  }

  handleAddressFormChange = (evt) => {
    const { name, value } = evt.target;
    this.setState((prev) => ({
      addressForm: { ...prev.addressForm, [name]: value },
    }));
  };

  handleSaveAddress = async (evt) => {
    evt.preventDefault();
    this.setState({ savingAddress: true, addressesError: null, addressSuccess: null });
    try {
      await axios.post('/api/addresses', {
        ...this.state.addressForm,
        isDefault: this.state.addresses.length === 0,
      });
      clearPendingAddress();
      this.setState({
        savingAddress: false,
        showAddressForm: false,
        addressForm: { ...emptyAddressForm },
        addressSuccess: 'Address saved.',
      });
      this.loadAddresses();
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        err.message ||
        'Unable to save address.';
      this.setState({ savingAddress: false, addressesError: message });
    }
  };

  handleSetDefault = async (id) => {
    try {
      await axios.patch(`/api/addresses/${id}`, { isDefault: true });
      this.loadAddresses();
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        'Unable to update address.';
      this.setState({ addressesError: message });
    }
  };

  handleDeleteAddress = async (id) => {
    try {
      await axios.delete(`/api/addresses/${id}`);
      this.loadAddresses();
    } catch (err) {
      const message =
        (err.response && err.response.data && err.response.data.error) ||
        'Unable to remove address.';
      this.setState({ addressesError: message });
    }
  };

  renderOrderHistory() {
    const { orders, ordersLoading, ordersError } = this.state;

    if (ordersLoading) {
      return (
        <div className="account-section-loading">
          <Spinner animation="border" size="sm" role="status" />
        </div>
      );
    }

    if (ordersError) {
      return <Alert variant="danger">{ordersError}</Alert>;
    }

    if (!orders.length) {
      return (
        <p className="account-section-copy">
          No orders yet.{' '}
          <Link to="/shop-all" className="Auth-link">
            Shop H.O.T Cocoa
          </Link>
        </p>
      );
    }

    return (
      <ul className="account-order-list">
        {orders.map((order) => {
          const ship = order.shippingAddress || {};
          const place = [ship.locality, ship.administrativeDistrictLevel1]
            .filter(Boolean)
            .join(', ');
          return (
            <li key={order.orderId} className="account-order">
              <div className="account-order-top">
                <span className="account-order-date">
                  {formatDate(order.paidAt || order.createdAt)}
                </span>
                <span className="account-order-status">
                  {statusLabel(order.status)}
                </span>
              </div>
              <div className="account-order-id">
                Order {String(order.orderId).slice(0, 8)}…
              </div>
              <ul className="account-order-items">
                {(order.lineItems || []).map((item) => (
                  <li key={`${order.orderId}-${item.productId}-${item.name}`}>
                    {item.name} × {item.quantity}
                  </li>
                ))}
              </ul>
              {place && (
                <div className="account-order-ship">Shipped to {place}</div>
              )}
              <div className="account-order-total">
                Total {formatCents(order.totalCents)}
              </div>
            </li>
          );
        })}
      </ul>
    );
  }

  renderDetails() {
    const { user } = this.props;
    return (
      <div className="account-details">
        <dl className="account-details-list">
          <div>
            <dt>Name</dt>
            <dd>
              {user.firstName} {user.lastName}
            </dd>
          </div>
          <div>
            <dt>Email</dt>
            <dd>{user.email}</dd>
          </div>
        </dl>
        <p className="account-section-copy">
          Need to change your password?{' '}
          <Link to="/forgot-password" className="Auth-link">
            Reset it here
          </Link>
          .
        </p>
      </div>
    );
  }

  renderAddressBook() {
    const {
      addresses,
      addressesLoading,
      addressesError,
      addressSuccess,
      showAddressForm,
      addressForm,
      savingAddress,
    } = this.state;

    return (
      <div className="account-address-book">
        {addressesError && <Alert variant="danger">{addressesError}</Alert>}
        {addressSuccess && <Alert variant="success">{addressSuccess}</Alert>}

        {addressesLoading ? (
          <div className="account-section-loading">
            <Spinner animation="border" size="sm" role="status" />
          </div>
        ) : (
          <>
            {!addresses.length && !showAddressForm && (
              <p className="account-section-copy">
                No addresses on file yet. Add one for faster checkout.
              </p>
            )}
            <ul className="account-address-list">
              {addresses.map((addr) => (
                <li key={addr.id} className="account-address">
                  {addr.label && (
                    <div className="account-address-label">{addr.label}</div>
                  )}
                  {formatAddressLines(addr).map((line) => (
                    <div key={line}>{line}</div>
                  ))}
                  <div className="account-address-actions">
                    {addr.isDefault ? (
                      <span className="account-address-default">Default</span>
                    ) : (
                      <button
                        type="button"
                        className="account-text-btn"
                        onClick={() => this.handleSetDefault(addr.id)}
                      >
                        Make default
                      </button>
                    )}
                    <button
                      type="button"
                      className="account-text-btn account-text-btn--danger"
                      onClick={() => this.handleDeleteAddress(addr.id)}
                    >
                      Remove
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

        {showAddressForm ? (
          <Form className="account-address-form" onSubmit={this.handleSaveAddress}>
            <Form.Group className="mb-2">
              <Form.Label>Label (optional)</Form.Label>
              <Form.Control
                name="label"
                value={addressForm.label}
                onChange={this.handleAddressFormChange}
                placeholder="Home, Work…"
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Full name</Form.Label>
              <Form.Control
                name="fullName"
                value={addressForm.fullName}
                onChange={this.handleAddressFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Street address</Form.Label>
              <Form.Control
                name="addressLine1"
                value={addressForm.addressLine1}
                onChange={this.handleAddressFormChange}
                required
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>Apartment, suite, etc.</Form.Label>
              <Form.Control
                name="addressLine2"
                value={addressForm.addressLine2}
                onChange={this.handleAddressFormChange}
              />
            </Form.Group>
            <Form.Group className="mb-2">
              <Form.Label>City</Form.Label>
              <Form.Control
                name="city"
                value={addressForm.city}
                onChange={this.handleAddressFormChange}
                required
              />
            </Form.Group>
            <Row>
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label>State</Form.Label>
                  <Form.Control
                    as="select"
                    name="state"
                    value={addressForm.state}
                    onChange={this.handleAddressFormChange}
                    required
                  >
                    <option value="">Select</option>
                    {US_STATES.map((code) => (
                      <option key={code} value={code}>
                        {code}
                      </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col xs={6}>
                <Form.Group className="mb-2">
                  <Form.Label>ZIP</Form.Label>
                  <Form.Control
                    name="postalCode"
                    value={addressForm.postalCode}
                    onChange={this.handleAddressFormChange}
                    required
                  />
                </Form.Group>
              </Col>
            </Row>
            <div className="account-address-form-actions">
              <Button
                type="submit"
                variant="success"
                size="sm"
                disabled={savingAddress}
              >
                {savingAddress ? 'Saving…' : 'Save address'}
              </Button>
              <Button
                type="button"
                variant="outline-secondary"
                size="sm"
                onClick={() =>
                  this.setState({
                    showAddressForm: false,
                    addressForm: { ...emptyAddressForm },
                  })
                }
              >
                Cancel
              </Button>
            </div>
          </Form>
        ) : (
          <Button
            variant="outline-success"
            size="sm"
            className="mt-2"
            onClick={() => this.setState({ showAddressForm: true })}
          >
            Add address
          </Button>
        )}
      </div>
    );
  }

  render() {
    const handleClick = this.props.handleClick;
    const user = this.props.user;
    const firstName = user.firstName;

    return (
      <Container className="Center-column account-page">
        <PageHeading icon="person">
          {user.isAdmin ? 'Admin Account' : 'Account'}
        </PageHeading>
        <div className="Words">Welcome to your account, {firstName}!</div>
        {user.isAdmin && (
          <Link to="/admin/inventory" className="Auth-link account-admin-link">
            Manage inventory
          </Link>
        )}
        <Accordion className="Accordian" defaultActiveKey="1">
          <Card>
            <Card.Header className="Card-header">
              <Accordion.Toggle as={Card.Header} variant="link" eventKey="1">
                <PageHeading as="span" icon="receipt">
                  Order History
                </PageHeading>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="1">
              <Card.Body>{this.renderOrderHistory()}</Card.Body>
            </Accordion.Collapse>
          </Card>

          <Card>
            <Card.Header className="Card-header">
              <Accordion.Toggle as={Card.Header} variant="link" eventKey="3">
                <PageHeading as="span" icon="pin">
                  Address Book
                </PageHeading>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="3">
              <Card.Body>{this.renderAddressBook()}</Card.Body>
            </Accordion.Collapse>
          </Card>

          <Card>
            <Card.Header className="Card-header">
              <Accordion.Toggle as={Card.Header} variant="link" eventKey="2">
                <PageHeading as="span" icon="id">
                  My Details
                </PageHeading>
              </Accordion.Toggle>
            </Card.Header>
            <Accordion.Collapse eventKey="2">
              <Card.Body>{this.renderDetails()}</Card.Body>
            </Accordion.Collapse>
          </Card>
        </Accordion>
        <a href="/login" className="Logout" onClick={handleClick}>
          Sign Out
        </a>
      </Container>
    );
  }
}

const mapState = (state) => {
  return {
    user: state.user,
  };
};

const mapDispatch = (dispatch) => {
  return {
    handleClick() {
      dispatch(logout());
    },
  };
};

export default connect(mapState, mapDispatch)(AccountInfo);

AccountInfo.propTypes = {
  handleClick: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
};
